/**
 * Identité de l’instance (CFG-01) — nom et logo affichés dans les en-têtes,
 * et état de service du théâtre (actif / suspendu / expiré).
 *
 * Rien n’est codé en dur : le même artefact installé pour un autre théâtre
 * affiche son nom (SPEC §1).
 *
 * ★ **Tout part de `GET /theatre/publique`, une route SANS session.**
 *
 * Ce fichier interrogeait `GET /configuration/theatre`, réservée au rôle
 * `admin`. Deux défauts en découlaient, tous deux mesurés :
 *
 *  - un **abonné** connecté par lien magique n’a pas d’`utilisateur` côté
 *    serveur : la garde répondait **401**, que `lib/api.ts` traduit en
 *    « session expirée » — la coque du portail se déconnectait toute seule et
 *    le parcours P5 échouait à chaque exécution ;
 *  - les rôles **guichet** et **lecture** prenaient un 403 et lisaient
 *    « Théâtre » en en-tête au lieu du nom de leur maison.
 *
 * Le repli ci-dessous existe donc toujours, mais il ne sert plus qu’au tout
 * premier rendu (requête en vol) ou à une API injoignable — jamais à masquer
 * un refus d’autorisation.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import type { Langue, TheatrePublique, TypeTenantPublic } from '@tb/shared'

import { api } from './api'
import { adresseSansTheatre } from '../features/accueil-theatre/racine'

/** Libellé de repli, surchargeable par instance sans toucher au code. */
export const NOM_THEATRE_PAR_DEFAUT: string =
  (import.meta.env.VITE_NOM_THEATRE as string | undefined) ?? 'Théâtre'

/**
 * Clé de `GET /configuration/theatre` — la vue **d’administration** complète
 * (adresse d’expéditeur, réglages de boutique, textes des documents).
 * Réservée au rôle `admin` : n’appartient qu’à `features/configuration`.
 */
export const CLE_THEATRE = ['configuration', 'theatre'] as const

/** Clé de `GET /theatre/publique` — l’en-tête et l’état, sans session. */
export const CLE_THEATRE_PUBLIQUE = ['theatre', 'publique'] as const

/**
 * La requête brute, partagée par `useTheatre()` et par la détection d’un
 * tenant expiré ou suspendu (`components/EtatTenant.tsx`). Même clé, donc
 * **une seule requête réseau** quel que soit le nombre d’appelants.
 *
 * `retry: false` : sur cette route, un échec n’est pas une avarie passagère —
 * c’est le refus de servir un théâtre expiré ou suspendu, et il faut le lire
 * tout de suite, pas au bout de trois tentatives.
 */
export function useTheatrePublique(actif = true): UseQueryResult<TheatrePublique> {
  return useQuery({
    queryKey: CLE_THEATRE_PUBLIQUE,
    queryFn: () => api.get<TheatrePublique>('/theatre/publique'),
    enabled: actif,
    // Le nom d’un théâtre change une fois par décennie : inutile de le rafraîchir.
    staleTime: 30 * 60_000,
    retry: false,
    // Sur l'apex, le 404 TENANT_INCONNU identifie durablement la vitrine.
    // Rejouer cette requête en revenant sur l'onglet ou après une reconnexion
    // effacerait l'erreur le temps du fetch : la racine afficherait alors son
    // écran de chargement et le visiteur perdrait sa position dans la page.
    // Une panne réseau reste, elle, réessayable à la reconnexion.
    refetchOnMount: (query) => !adresseSansTheatre(query.state.error),
    refetchOnReconnect: (query) => !adresseSansTheatre(query.state.error),
  })
}

export interface UtiliseTheatre {
  readonly nom: string
  readonly logo: string | null
  /**
   * ★ EVT-04 — la nature du tenant. `client` tant que la requête est en vol ou
   * a échoué : l'interface complète est le défaut, on n'ampute jamais un menu
   * par accident de réseau.
   */
  readonly type: TypeTenantPublic
  /**
   * ★★ FAC-05 — une facture accompagnera-t-elle la commande ?
   *
   * `false` tant que la requête est en vol ou a échoué, et c'est le sens
   * inverse du défaut de `type` — délibérément. Amputer un menu par accident
   * de réseau prive d'une fonction ; promettre une facture par accident de
   * réseau fait dire à l'écran quelque chose de faux au moment précis où
   * l'acheteur s'engage. **Dans le doute, on promet moins.**
   */
  readonly factureDisponible: boolean
  /**
   * ★★ I18N-01 — la langue de la maison, ou `null` tant qu'on ne la sait pas.
   *
   * `null` et non `'fr'` : la distinction porte une décision. Sur la vitrine
   * (l'apex, aucun théâtre) et pendant que la requête vole, il n'y a pas de
   * langue de théâtre — et `useLangue()` doit alors écouter le NAVIGATEUR du
   * visiteur. Un défaut `'fr'` écraserait ce signal et servirait du français
   * à un prospect alémanique dont le navigateur disait `de-CH`.
   */
  readonly langue: Langue | null
}

/**
 * Nom et logo du théâtre. Ne lève jamais : un en-tête n’est pas un écran
 * critique, et il doit se dessiner avant même qu’une session existe.
 */
export function useTheatre(actif = true): UtiliseTheatre {
  const requete = useTheatrePublique(actif)

  const nom = requete.data?.nom
  return {
    nom: nom !== undefined && nom !== '' ? nom : NOM_THEATRE_PAR_DEFAUT,
    logo: requete.data?.logo ?? null,
    type: requete.data?.type ?? 'client',
    factureDisponible: requete.data?.factureDisponible ?? false,
    langue: requete.data?.langue ?? null,
  }
}
